// src/Components/Modules/Purchase/PurchaseReturns.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, FormControl, InputLabel, Select, MenuItem, IconButton
} from '@mui/material';
import { FaPlus, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function PurchaseReturns() {
    const [returns, setReturns] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [items, setItems] = useState([{ item_name: '', quantity: 1, unit_price: 0 }]);
    const [form, setForm] = useState({ vendor_id: '', return_date: new Date().toISOString().split('T')[0], reason: '' });

    const fetchData = async () => {
        const [rRes, vRes] = await Promise.all([
            AxiosAuthInstance.get('purchase-returns'),
            AxiosAuthInstance.get('vendors')
        ]);
        setReturns(rRes.data.returns || []);
        setVendors(vRes.data.vendors || []);
    };

    useEffect(() => { fetchData(); }, []);

    const addItem = () => setItems([...items, { item_name: '', quantity: 1, unit_price: 0 }]);
    const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
    const updateItem = (idx, field, val) => {
        const newItems = [...items];
        newItems[idx][field] = val;
        setItems(newItems);
    };

    const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

    const handleSave = async () => {
        try {
            await AxiosAuthInstance.post('purchase-returns', { ...form, items });
            Swal.fire({ icon: 'success', title: 'Return Processed!', timer: 1500, showConfirmButton: false });
            setDialogOpen(false);
            setItems([{ item_name: '', quantity: 1, unit_price: 0 }]);
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Purchase Returns" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700}>Purchase Returns</Typography>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => setDialogOpen(true)}>New Return</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell align="right">Total Amount</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {returns.map((r) => (
                            <TableRow key={r.id} hover>
                                <TableCell>{r.return_date}</TableCell>
                                <TableCell>{r.vendor?.vendor_name || '-'}</TableCell>
                                <TableCell>{r.reason || '-'}</TableCell>
                                <TableCell align="right">৳{Number(r.total_amount).toLocaleString()}</TableCell>
                                <TableCell><Chip label={r.status} size="small" color="success" /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>New Purchase Return</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Vendor</InputLabel>
                            <Select value={form.vendor_id} label="Vendor" onChange={(e) => setForm({...form, vendor_id: e.target.value})}>
                                <MenuItem value="">Select Vendor</MenuItem>
                                {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.vendor_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Return Date" type="date" size="small" value={form.return_date} onChange={(e) => setForm({...form, return_date: e.target.value})} fullWidth InputLabelProps={{shrink: true}} />
                        </Box>
                        <TextField label="Reason for Return" multiline rows={2} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} fullWidth />
                        
                        <Typography variant="subtitle2" fontWeight={600} sx={{mt: 1}}>Items</Typography>
                        {items.map((item, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField size="small" label="Item Name" value={item.item_name} onChange={(e) => updateItem(idx, 'item_name', e.target.value)} sx={{flex: 3}} />
                                <TextField size="small" type="number" label="Qty" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} sx={{flex: 1}} />
                                <TextField size="small" type="number" label="Price" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', Number(e.target.value))} sx={{flex: 1}} />
                                <IconButton onClick={() => removeItem(idx)} disabled={items.length === 1}><FaTrash size={14} /></IconButton>
                            </Box>
                        ))}
                        <Button variant="outlined" size="small" startIcon={<FaPlus />} onClick={addItem} sx={{alignSelf: 'flex-start'}}>Add Item</Button>
                        
                        <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, textAlign: 'right' }}>
                            <Typography variant="h6" fontWeight={700} color="error.main">Total Return: ৳{totalAmount.toLocaleString()}</Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Process Return</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}